import React, { useState, useEffect } from 'react';
import { Heart, Trash2 } from 'lucide-react';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner, EmptyState } from '../components/ui';
import LicenseGate from '../components/ui/LicenseGate';
import { FavoriteComponent } from '../types/customDashboard';
import { TimePeriod } from '../types/api';
import { logger } from '../utils/logger';
import config from '../config/api.config';

// Import all components
import { TrendAnalysisChart, CreditComparisonChart, PerformanceTable } from '../components/executive';
import { 
  MonthlySalesComparison,
  CustomerSatisfactionComparison,
  SalesTrend,
  CustomerSatisfactionAnalysis,
  ProductCategorization,
  CustomerRetentionAnalysis
} from '../components/mainPerformance';
import {
  PurchasePercentagesChart,
  TotalOrdersNonOrders,
  TotalCustomerOrders,
  OrdersLastTwoMonths,
  CustomerTrendsChart,
  CustomerBalanceChart,
  CustomerTrendsWithBalanceChart,
  NoOrdersLastTwoMonths,
  CustomerReportSection,
  AveragePurchasesTable,
  AveragePurchasesYoYTable
} from '../components/customerAnalytics';
import {
  PersonnelPerformanceDistribution,
  TeamComposition,
  PersonnelPerformanceByRole,
  ComparisonPerformanceByRole,
  KeyPerformanceMetrics,
  TopPersonnelPerformance
} from '../components/hr';
import {
  ComplaintReviewStatus,
  DissatisfactionReasons,
  DissatisfiedProducts,
  SurveyResponsesByType
} from '../components/refineCustomer';
import {
  OverallSatisfaction,
  DetailedSatisfaction,
  OrderTypeSatisfaction,
  ShiftSatisfaction
} from '../components/satisfaction';
import {
  OverallSubscription,
  OrderTypeSubscription,
  ShiftSubscription
} from '../components/subscription';
import SubscriptionDashboard from './SubscriptionDashboard';
import { ProductCategorySalesTrend, MonthlyPerformanceTable } from '../components/club';
import {
  ClubConditionsTable,
  UsedCreditRatioChart,
  GeneratedCreditRatioChart,
  GeneratedCreditAmountRatioChart,
  ParticipationRateChart,
  ParticipationAmountChart
} from '../components/clubConditions';
import MarketPulseKpiSection from '../components/marketPulse/MarketPulseKpiSection';
import MarketPulseTableSection from '../components/marketPulse/MarketPulseTableSection';
import MarketPulseYoYTableSection from '../components/marketPulse/MarketPulseYoYTableSection';
import MarketPulseTrendSection from '../components/marketPulse/MarketPulseTrendSection';

// Component map
const componentMap: Record<string, React.ComponentType<any>> = {
  // Executive Dashboard
  'trend-analysis-chart': TrendAnalysisChart,
  'credit-comparison-chart': CreditComparisonChart,
  'executive-performance-table': PerformanceTable,
  
  // Main Performance Dashboard
  'monthly-sales-comparison': MonthlySalesComparison,
  'customer-satisfaction-comparison': CustomerSatisfactionComparison,
  'sales-trend': SalesTrend,
  'customer-satisfaction-analysis': CustomerSatisfactionAnalysis,
  'product-categorization': ProductCategorization,
  'customer-retention-analysis': CustomerRetentionAnalysis,
  
  // Customer Analytics Dashboard
  'purchase-percentages-chart': PurchasePercentagesChart,
  'total-orders-non-orders': TotalOrdersNonOrders,
  'total-customer-orders': TotalCustomerOrders,
  'orders-last-two-months': OrdersLastTwoMonths,
  'customer-trends-chart': CustomerTrendsChart,
  'customer-balance-chart': CustomerBalanceChart,
  'customer-trends-balance-chart': CustomerTrendsWithBalanceChart,
  'no-orders-last-two-months': NoOrdersLastTwoMonths,
  'customer-report-section': CustomerReportSection,
  'average-purchases-table': AveragePurchasesTable,
  'average-purchases-yoy-table': AveragePurchasesYoYTable,
  
  // HR Dashboard
  'personnel-performance-distribution': PersonnelPerformanceDistribution,
  'team-composition': TeamComposition,
  'personnel-performance-by-role': PersonnelPerformanceByRole,
  'comparison-performance-by-role': ComparisonPerformanceByRole,
  'key-performance-metrics': KeyPerformanceMetrics,
  'top-personnel-performance': TopPersonnelPerformance,
  
  // Refine Customer Dashboard
  'complaint-review-status': ComplaintReviewStatus,
  'dissatisfaction-reasons': DissatisfactionReasons,
  'dissatisfied-products': DissatisfiedProducts,
  'survey-responses-by-type': SurveyResponsesByType,
  
  // Satisfaction Dashboard
  'overall-satisfaction': OverallSatisfaction,
  'detailed-satisfaction': DetailedSatisfaction,
  'order-type-satisfaction': OrderTypeSatisfaction,
  'shift-satisfaction': ShiftSatisfaction,

  // Subscription Dashboard
  'subscription-dashboard': SubscriptionDashboard,
  'overall-subscription': OverallSubscription,
  'order-type-subscription': OrderTypeSubscription,
  'shift-subscription': ShiftSubscription,
  
  // Club Dashboard
  'product-category-sales-trend': ProductCategorySalesTrend,
  'monthly-performance-table': MonthlyPerformanceTable,
  
  // Club Conditions Dashboard
  'club-conditions-table': ClubConditionsTable,
  'used-credit-ratio-chart': UsedCreditRatioChart,
  'generated-credit-ratio-chart': GeneratedCreditRatioChart,
  'generated-credit-amount-ratio-chart': GeneratedCreditAmountRatioChart,
  'participation-rate-chart': ParticipationRateChart,
  'participation-amount-chart': ParticipationAmountChart,

  // Market Pulse Dashboard
  'market-pulse-kpi': MarketPulseKpiSection,
  'market-pulse-table': MarketPulseTableSection,
  'market-pulse-yoy-table': MarketPulseYoYTableSection,
  'market-pulse-trend': MarketPulseTrendSection,
};

interface TailwindCustomDashboardProps {
  selectedBrand?: { id: number; name: string; logo?: string } | null;
  selectedBranch?: { id: number; name: string; brandId: number } | null;
  selectedPeriod?: string;
}

interface DashboardItem {
  id: string;
  favoriteId: string;
  gridClass: string;
}

const TailwindCustomDashboard: React.FC<TailwindCustomDashboardProps> = ({
  selectedBrand,
  selectedBranch,
  selectedPeriod
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [dashboardItems, setDashboardItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { favorites } = useFavorites();
  const { user } = useAuth();
  
  // Utility function to convert UI timeFrame to API timeFrame format
  const convertToApiTimeFrame = (uiTimeFrame: string): TimePeriod => {
    switch (uiTimeFrame) {
      case 'last1Month': return '1month';
      case 'last3Months': return '3months';
      case 'last6Months': return '6months';
      case 'lastYear': return '12months';
      default: return '6months';
    }
  };
  
  // Load dashboard from localStorage on mount - user specific
  useEffect(() => {
    const isAuthDisabled = config.STAGE_NO_AUTH;
    const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
    if (userId) {
      loadDashboard();
    } else {
      // If no user ID available, just set loading to false
      setLoading(false);
    }
  }, [user?.id]);
  
  const loadDashboard = () => {
    try {
      setLoading(true);
      const isAuthDisabled = config.STAGE_NO_AUTH;
      const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
      if (!userId) {
        setLoading(false);
        return;
      }
      
      const storageKey = `tailwind_custom_dashboard_${userId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        setDashboardItems(parsed.items || []);
      }
    } catch (error) {
      logger.error('Error loading dashboard', 'TailwindCustomDashboard', error);
    } finally {
      setLoading(false);
    }
  };
  
  const saveDashboard = () => {
    try {
      const isAuthDisabled = config.STAGE_NO_AUTH;
      const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
      if (!userId) {
        logger.warn('Cannot save dashboard - no user ID available', 'TailwindCustomDashboard');
        return;
      }
      
      const storageKey = `tailwind_custom_dashboard_${userId}`;
      const dashboardData = {
        items: dashboardItems,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(dashboardData));
      setIsEditing(false);
    } catch (error) {
      logger.error('Error saving dashboard', 'TailwindCustomDashboard', error);
    }
  };
  
  const addToDashboard = (favorite: FavoriteComponent) => {
    // Determine grid class based on the component's original layout
    const gridClass = favorite.gridLayout?.containerClass || 'grid grid-cols-1 gap-6';
    
    const newItem: DashboardItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      favoriteId: favorite.id,
      gridClass: gridClass
    };
    
    setDashboardItems([...dashboardItems, newItem]);
  };
  
  const removeFromDashboard = (itemId: string) => {
    setDashboardItems(dashboardItems.filter(item => item.id !== itemId));
  };
  
  const renderComponent = (item: DashboardItem) => {
    const favorite = favorites.find(f => f.id === item.favoriteId);
    if (!favorite) return null;
    
    const Component = componentMap[favorite.componentId];
    if (!Component) {
      logger.warn(`Component not found in componentMap: ${favorite.componentId}`, 'TailwindCustomDashboard');
      logger.debug('Available components', 'TailwindCustomDashboard', Object.keys(componentMap));
      
      return (
        <div className="bg-bg-primary rounded-lg border border-border p-6 text-center">
          <p className="text-text-secondary">{favorite.title}</p>
          <p className="text-text-tertiary text-sm mt-1">کامپوننت در حال توسعه…</p>
          <p className="text-xs text-text-tertiary mt-2">
            Component ID: {favorite.componentId}
          </p>
        </div>
      );
    }
    
    // Prepare props based on the dashboard source
    const componentProps: any = {
      ...favorite.props,
      branchId: selectedBranch?.id || 0,
      compact: true,
      showLoading: true
    };
    
    // Add currencySymbol for club conditions table
    if (favorite.componentId === 'club-conditions-table' && favorite.props.currencySymbol) {
      componentProps.currencySymbol = favorite.props.currencySymbol;
    }
    
    // Add useCustomDashboardData flag for club conditions components
    if ([
      'club-conditions-table',
      'used-credit-ratio-chart',
      'generated-credit-ratio-chart',
      'generated-credit-amount-ratio-chart',
      'participation-rate-chart',
      'participation-amount-chart'
    ].includes(favorite.componentId)) {
      componentProps.useCustomDashboardData = true;
    }
    
    // Add brand-specific props
    if (selectedBrand) {
      componentProps.brandId = selectedBrand.id;
      componentProps.selectedBrand = selectedBrand;
    }
    
    // Add time-related props based on dashboard source
    if (selectedPeriod) {
      // For components that need timeRange
      if (['monthly-sales-comparison', 'customer-satisfaction-comparison', 'sales-trend', 
          'customer-satisfaction-analysis', 'product-categorization', 'customer-retention-analysis'].includes(favorite.componentId)) {
        componentProps.timeRange = selectedPeriod;
      }
      
      // For components that need customTimeRange (API format)
      if (['purchase-percentages-chart', 'total-orders-non-orders', 'total-customer-orders',
          'orders-last-two-months', 'customer-trends-chart', 'customer-balance-chart',
          'no-orders-last-two-months', 'customer-report-section', 'average-purchases-table',
          'average-purchases-yoy-table', 'market-pulse-kpi', 'market-pulse-table', 'market-pulse-yoy-table', 'market-pulse-trend'].includes(favorite.componentId)) {
        componentProps.customTimeRange = convertToApiTimeFrame(selectedPeriod);
      }
      
      // For satisfaction components that need timeRange with API format
      if (['overall-satisfaction', 'detailed-satisfaction', 'order-type-satisfaction',
          'shift-satisfaction', 'overall-subscription', 'order-type-subscription',
          'shift-subscription'].includes(favorite.componentId)) {
        componentProps.timeRange = convertToApiTimeFrame(selectedPeriod);
      }

      if (favorite.componentId === 'subscription-dashboard') {
        componentProps.timePeriod = selectedPeriod;
      }
      
      // For components that need selectedPeriod
      if (['trend-analysis-chart', 'credit-comparison-chart', 'executive-performance-table',
          'personnel-performance-distribution', 'team-composition', 'personnel-performance-by-role',
          'comparison-performance-by-role', 'key-performance-metrics', 'top-personnel-performance'].includes(favorite.componentId)) {
        componentProps.selectedPeriod = selectedPeriod;
      }
      
      // For club components that need timeRange (API format)
      if (['product-category-sales-trend', 'monthly-performance-table'].includes(favorite.componentId)) {
        componentProps.timeRange = convertToApiTimeFrame(selectedPeriod);
      }
      
      // For club conditions components that need timeRange (API format)
      if (['club-conditions-table', 'used-credit-ratio-chart', 'generated-credit-ratio-chart', 
          'generated-credit-amount-ratio-chart', 'participation-rate-chart', 
          'participation-amount-chart'].includes(favorite.componentId)) {
        componentProps.timeRange = convertToApiTimeFrame(selectedPeriod);
      }
    }
    
    // Add selectedBranch for components that need it
    if (selectedBranch) {
      componentProps.selectedBranch = selectedBranch;
    }
    
    return (
      <Component {...componentProps} />
    );
  };
  
  // Group items by their grid class
  const groupedItems = dashboardItems.reduce((acc, item) => {
    const gridClass = item.gridClass;
    if (!acc[gridClass]) {
      acc[gridClass] = [];
    }
    acc[gridClass].push(item);
    return acc;
  }, {} as Record<string, DashboardItem[]>);
  
  if (!selectedBranch?.id) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-4 md:space-y-6 animate-enter-up">
          <div className="card-base card-padding border border-status-warning" role="alert">
            <h3 className="title-card text-status-warning">برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.</h3>
            <p className="text-text-secondary">برای مشاهده داشبورد شخصی، ابتدا باید یک شعبه را انتخاب نمایید.</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری داشبورد…"
          />
        </div>
      </div>
    );
  }
  
  return (
    <LicenseGate licenseType="club" dashboardName="داشبورد شخصی">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Header */}
          <div className="flex justify-center items-center">
            <h1 className="title-dashboard">داشبورد شخصی</h1>
            
            {/* <div className="flex gap-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Edit2 size={20} />
                  <span>ویرایش داشبورد</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      loadDashboard(); // Reset changes
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <X size={20} />
                    <span>انصراف</span>
                  </button>
                  
                  <button
                    onClick={saveDashboard}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={20} />
                    <span>ذخیره تغییرات</span>
                  </button>
                </>
              )}
            </div> */}
          </div>
          
          {/* Edit Mode - Favorites List */}
          {isEditing && (
            <div className="bg-bg-secondary rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                کامپوننت‌های علاقه‌مندی
              </h3>
              
              {favorites.length === 0 ? (
                <p className="text-text-secondary text-center py-8">
                  هنوز کامپوننتی را به علاقه‌مندی‌ها اضافه نکرده‌اید.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favorites.map((favorite) => {
                    const isAdded = dashboardItems.some(item => item.favoriteId === favorite.id);
                    return (
                      <div
                        key={favorite.id}
                        className={`p-4 rounded-lg border transition-all cursor-pointer ${
                          isAdded 
                            ? 'bg-primary/10 border-primary' 
                            : 'bg-bg-primary border-border hover:border-primary'
                        }`}
                        onClick={() => !isAdded && addToDashboard(favorite)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-text-primary">
                              {favorite.title}
                            </h4>
                            <p className="text-sm text-text-secondary mt-1">
                              {favorite.dashboardSource}
                            </p>
                          </div>
                          <Heart 
                            size={20} 
                            className={isAdded ? 'text-primary fill-primary' : 'text-text-tertiary'}
                          />
                        </div>
                        {isAdded && (
                          <p className="text-xs text-primary mt-2">اضافه شده به داشبورد</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Dashboard Content */}
          {dashboardItems.length === 0 ? (
            <div className="card-base card-padding text-center py-16">
              <EmptyState
                message="داشبورد شما خالی است"
                icon="📊"
              />
              {/* <p className="text-text-secondary text-sm mt-2 mb-6">
                برای شروع، روی دکمه ویرایش کلیک کنید و کامپوننت‌های مورد علاقه خود را اضافه نمایید.
              </p>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary flex items-center gap-2 mx-auto"
                >
                  <Plus size={20} />
                  <span>شروع ویرایش</span>
                </button>
              )} */}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedItems).map(([gridClass, items]) => (
                <div key={gridClass} className={gridClass}>
                  {items.map((item) => (
                    <div key={item.id} className="relative">
                      {isEditing && (
                        <button
                          onClick={() => removeFromDashboard(item.id)}
                          className="absolute top-2 right-2 z-10 p-2 bg-bg-primary rounded-lg shadow-md hover:bg-status-danger/10 transition-colors"
                          title="حذف"
                        >
                          <Trash2 size={18} className="text-status-danger" />
                        </button>
                      )}
                      {renderComponent(item)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default TailwindCustomDashboard;
